import { useState } from "react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/States";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { Field, FormCard, ListState, Pick, post, useList, useMembersByRole, useSubmit, AC } from "./shared";

const REL = { ayah: "Ayah", ibu: "Ibu", wali: "Wali" };
const GSTATUS = { pending: { label: "Menunggu Verifikasi", tone: "warn" }, verified: { label: "Terverifikasi", tone: "ok" }, revoked: { label: "Dicabut", tone: "muted" } };

export const AssignmentsTab = ({ canManage }) => {
  const q = useList(`${AC}/assignments`);
  const classes = useList(`${AC}/classes`);
  const subjects = useList(`${AC}/subjects`);
  const teachers = useMembersByRole(["guru", "wali_kelas"]);
  const s = useSubmit();
  const [f, setF] = useState({ class_id: "", subject_id: "", teacher_membership_id: "" });
  const [end, setEnd] = useState(null);
  const add = () => s.run(() => post("/assignments", f), "Penugasan guru disimpan.").then((ok) => ok && setF({ class_id: "", subject_id: "", teacher_membership_id: "" }));
  return (
    <div className="space-y-5">
      {canManage && (
        <FormCard title="Tugaskan guru" onSubmit={add} busy={s.busy} err={s.err} submitLabel="Simpan penugasan" testId="assignment-form">
          <Pick label="Kelas" value={f.class_id} onChange={(v) => setF({ ...f, class_id: v })} options={(classes.data || []).map((k) => ({ value: k.id, label: k.name }))} testId="assignment-class-select" />
          <Pick label="Mata pelajaran" value={f.subject_id} onChange={(v) => setF({ ...f, subject_id: v })} options={(subjects.data || []).map((x) => ({ value: x.id, label: x.name }))} testId="assignment-subject-select" />
          <Pick label="Guru" value={f.teacher_membership_id} onChange={(v) => setF({ ...f, teacher_membership_id: v })} options={teachers.map((t) => ({ value: t.id, label: t.full_name }))} testId="assignment-teacher-select" />
        </FormCard>
      )}
      <ListState q={q} empty={{ title: "Belum ada penugasan guru" }}>
        {(rows) => (
          <ul className="divide-y rounded-2xl border bg-card">
            {rows.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-4" data-testid={`assignment-${r.id}`}>
                <p className="text-sm"><b>{r.subject_name}</b> · {r.class_name} · {r.teacher_name}</p>
                {canManage && <Button size="sm" variant="outline" onClick={() => setEnd(r)} data-testid={`assignment-end-${r.id}`}>Akhiri</Button>}
              </li>
            ))}
          </ul>
        )}
      </ListState>
      <ConfirmReasonDialog open={!!end} onOpenChange={(o) => !o && setEnd(null)} danger title="Akhiri penugasan?" confirmLabel="Akhiri" testId="assignment-end-dialog"
        description={end ? `${end.teacher_name} tidak lagi mengajar ${end.subject_name} di ${end.class_name}. Riwayat tetap tersimpan.` : ""}
        onConfirm={(reason) => post(`/assignments/${end.id}/end`, { reason }).then(() => q.refetch())} />
    </div>
  );
};

export const GuardiansTab = () => {
  const q = useList(`${AC}/guardians`);
  const guardians = useMembersByRole(["orang_tua"]);
  const students = useMembersByRole(["siswa"]);
  const s = useSubmit();
  const [f, setF] = useState({ guardian_membership_id: "", student_membership_id: "", relation: "", evidence_note: "" });
  const [act, setAct] = useState(null);
  const add = () => s.run(() => post("/guardians", f), "Relasi dibuat dan menunggu verifikasi.")
    .then((ok) => ok && setF({ guardian_membership_id: "", student_membership_id: "", relation: "", evidence_note: "" }));
  return (
    <div className="space-y-5">
      <FormCard title="Ajukan relasi orang tua/wali – siswa" onSubmit={add} busy={s.busy} err={s.err} submitLabel="Ajukan relasi" testId="guardian-form">
        <Pick label="Orang tua/wali" value={f.guardian_membership_id} onChange={(v) => setF({ ...f, guardian_membership_id: v })} options={guardians.map((g) => ({ value: g.id, label: g.full_name }))} testId="guardian-parent-select" />
        <Pick label="Siswa" value={f.student_membership_id} onChange={(v) => setF({ ...f, student_membership_id: v })} options={students.map((g) => ({ value: g.id, label: g.full_name }))} testId="guardian-student-select" />
        <Pick label="Hubungan" value={f.relation} onChange={(v) => setF({ ...f, relation: v })} options={Object.entries(REL).map(([value, label]) => ({ value, label }))} testId="guardian-relation-select" />
        <Field id="g-evidence" label="Bukti yang diperiksa" placeholder="Contoh: kartu keluarga" value={f.evidence_note} onChange={(e) => setF({ ...f, evidence_note: e.target.value })} data-testid="guardian-evidence-input" />
      </FormCard>
      <p className="text-xs text-muted-foreground">Orang tua/wali hanya dapat melihat data anak setelah relasi diverifikasi.</p>
      <ListState q={q} empty={{ title: "Belum ada relasi orang tua/wali" }}>
        {(rows) => (
          <ul className="divide-y rounded-2xl border bg-card">
            {rows.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-4" data-testid={`guardian-${r.id}`}>
                <div className="text-sm"><b>{r.guardian_name}</b> ({REL[r.relation]}) → {r.student_name}</div>
                <div className="flex items-center gap-2">
                  <StatusBadge map={GSTATUS} value={r.status} testId={`guardian-status-${r.id}`} />
                  {r.status === "pending" && <Button size="sm" onClick={() => setAct({ r, kind: "verify" })} data-testid={`guardian-verify-${r.id}`}>Verifikasi</Button>}
                  {r.status !== "revoked" && <Button size="sm" variant="outline" onClick={() => setAct({ r, kind: "revoke" })} data-testid={`guardian-revoke-${r.id}`}>Cabut</Button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </ListState>
      <ConfirmReasonDialog open={!!act} onOpenChange={(o) => !o && setAct(null)} danger={act?.kind === "revoke"} testId="guardian-action-dialog"
        title={act?.kind === "verify" ? "Verifikasi relasi?" : "Cabut relasi?"} confirmLabel={act?.kind === "verify" ? "Verifikasi" : "Cabut"}
        description={act ? `${act.r.guardian_name} → ${act.r.student_name}` : ""}
        onConfirm={(reason) => post(`/guardians/${act.r.id}/${act.kind}`, { reason }).then(() => q.refetch())} />
    </div>
  );
};
